import React, { useState, useEffect } from 'react';
import { getAllProfessions, Profession } from '../api/Professions';
import { 
  getStateRequirementsForProfession, 
  createStateRequirement, 
  updateStateRequirement, 
  deleteStateRequirement,
  IStateRequirement,
  ICategory
} from '../api/StateRequirements';
import { getAllLicStates } from '../api/States';
import { createCategory, updateCategory } from '../api/Categories';
import { LicState } from '../types';
import { useAuth } from '../useAuth';
import api from '../utils/api';
import StateRequirementForm from '../components/StateRequirementForm';

// All 50 US states
const ALL_US_STATES: LicState[] = [
  { _id: '1', stateID: 1, state: 'Alabama' },
  { _id: '2', stateID: 2, state: 'Alaska' },
  { _id: '3', stateID: 3, state: 'Arizona' },
  { _id: '4', stateID: 4, state: 'Arkansas' },
  { _id: '5', stateID: 5, state: 'California' },
  { _id: '6', stateID: 6, state: 'Colorado' },
  { _id: '7', stateID: 7, state: 'Connecticut' },
  { _id: '8', stateID: 8, state: 'Delaware' },
  { _id: '9', stateID: 9, state: 'Florida' },
  { _id: '10', stateID: 10, state: 'Georgia' },
  { _id: '11', stateID: 11, state: 'Hawaii' },
  { _id: '12', stateID: 12, state: 'Idaho' },
  { _id: '13', stateID: 13, state: 'Illinois' },
  { _id: '14', stateID: 14, state: 'Indiana' },
  { _id: '15', stateID: 15, state: 'Iowa' },
  { _id: '16', stateID: 16, state: 'Kansas' },
  { _id: '17', stateID: 17, state: 'Kentucky' },
  { _id: '18', stateID: 18, state: 'Louisiana' },
  { _id: '19', stateID: 19, state: 'Maine' },
  { _id: '20', stateID: 20, state: 'Maryland' },
  { _id: '21', stateID: 21, state: 'Massachusetts' },
  { _id: '22', stateID: 22, state: 'Michigan' },
  { _id: '23', stateID: 23, state: 'Minnesota' },
  { _id: '24', stateID: 24, state: 'Mississippi' },
  { _id: '25', stateID: 25, state: 'Missouri' },
  { _id: '26', stateID: 26, state: 'Montana' },
  { _id: '27', stateID: 27, state: 'Nebraska' },
  { _id: '28', stateID: 28, state: 'Nevada' },
  { _id: '29', stateID: 29, state: 'New Hampshire' },
  { _id: '30', stateID: 30, state: 'New Jersey' },
  { _id: '31', stateID: 31, state: 'New Mexico' },
  { _id: '32', stateID: 32, state: 'New York' },
  { _id: '33', stateID: 33, state: 'North Carolina' },
  { _id: '34', stateID: 34, state: 'North Dakota' },
  { _id: '35', stateID: 35, state: 'Ohio' },
  { _id: '36', stateID: 36, state: 'Oklahoma' },
  { _id: '37', stateID: 37, state: 'Oregon' },
  { _id: '38', stateID: 38, state: 'Pennsylvania' },
  { _id: '39', stateID: 39, state: 'Rhode Island' },
  { _id: '40', stateID: 40, state: 'South Carolina' },
  { _id: '41', stateID: 41, state: 'South Dakota' },
  { _id: '42', stateID: 42, state: 'Tennessee' },
  { _id: '43', stateID: 43, state: 'Texas' },
  { _id: '44', stateID: 44, state: 'Utah' },
  { _id: '45', stateID: 45, state: 'Vermont' },
  { _id: '46', stateID: 46, state: 'Virginia' },
  { _id: '47', stateID: 47, state: 'Washington' },
  { _id: '48', stateID: 48, state: 'West Virginia' },
  { _id: '49', stateID: 49, state: 'Wisconsin' },
  { _id: '50', stateID: 50, state: 'Wyoming' },
];

const ProfessionManagement = () => {
  const { user } = useAuth();
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Profession>>({
    professionID: '',
    name: '',
    description: '',
    creditTerminology: 'CE',
    isActive: true,
  });

  // State requirements management
  const [allStates, setAllStates] = useState<LicState[]>([]);
  const [stateRequirements, setStateRequirements] = useState<IStateRequirement[]>([]);
  const [showStateForm, setShowStateForm] = useState(false);
  const [editingRequirement, setEditingRequirement] = useState<Partial<IStateRequirement> | null>(null);
  const [selectedStateID, setSelectedStateID] = useState<number | null>(null);

  const fetchProfessions = async () => {
    try {
      setLoading(true);
      // Fetch all professions including inactive ones for admin management
  const response = await api.get('/api/professions?includeInactive=true');
      setProfessions(response.data);
      setError('');
    } catch (err: any) {
      setError('Failed to fetch professions.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStateRequirements = async (professionID: string) => {
    try {
      const requirements = await getStateRequirementsForProfession(professionID);
      setStateRequirements(requirements);
    } catch (err) {
      console.error('Failed to fetch state requirements:', err);
      setStateRequirements([]);
    }
  };

  useEffect(() => {
    fetchProfessions();
    // Fetch actual states from database instead of hardcoded list
    const fetchStates = async () => {
      try {
        const states = await getAllLicStates();
        setAllStates(states);
      } catch (err) {
        console.error('Failed to fetch states:', err);
        setError('Failed to load states from database');
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (isEditing && formData.professionID) {
      fetchStateRequirements(formData.professionID);
    } else {
      setStateRequirements([]);
    }
  }, [isEditing, formData.professionID]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const resetForm = () => {
    setIsEditing(null);
    setFormData({
      professionID: '',
      name: '',
      description: '',
      creditTerminology: 'CE',
      isActive: true,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      if (isEditing) {
        // Update existing profession
        await api.put(`/api/professions/${isEditing}`, {
          name: formData.name,
          description: formData.description,
          creditTerminology: formData.creditTerminology,
          isActive: formData.isActive,
        });
        setSuccessMessage('Profession updated successfully!');
      } else {
        // Create new profession
        if (!formData.name || !formData.creditTerminology) {
          setError('Please fill in all required fields (Name, Credit Terminology)');
          return;
        }
  await api.post('/api/professions', formData);
        setSuccessMessage('Profession created successfully!');
      }
      resetForm();
      fetchProfessions();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Failed to save profession.';
      setError(message);
      console.error(err);
    }
  };

  const handleEdit = (profession: Profession) => {
    setIsEditing(profession._id);
    setFormData({
      professionID: profession.professionID,
      name: profession.name,
      description: profession.description || '',
      creditTerminology: profession.creditTerminology,
      isActive: profession.isActive !== undefined ? profession.isActive : true,
    });
  };

  const handleDelete = async (professionId: string) => {
    if (window.confirm('Are you sure you want to deactivate this profession? This will soft-delete it (set to inactive).')) {
      try {
  await api.delete(`/api/professions/${professionId}`);
        setSuccessMessage('Profession deactivated successfully!');
        fetchProfessions();
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        setError('Failed to deactivate profession.');
        console.error(err);
      }
    }
  };

  // State requirement handlers
  const handleAddState = (stateID: number) => {
    setSelectedStateID(stateID);
    setEditingRequirement({
      professionID: formData.professionID,
      stateID: stateID,
      totalCreditsRequired: 0,
      cycleLengthYears: 2,
      effectiveDate: new Date().toISOString(),
    });
    setShowStateForm(true);
  };

  const handleEditStateRequirement = (requirement: IStateRequirement) => {
    setSelectedStateID(requirement.stateID);
    setEditingRequirement(requirement);
    setShowStateForm(true);
  };

  const handleSaveStateRequirement = async (data: Partial<IStateRequirement>) => {
    try {
      let categoryIDs: string[] = [];
      
      // If there are new categories to create (those without _id), create them first
      if (data.categoryIDs && Array.isArray(data.categoryIDs)) {
        const newCategories = data.categoryIDs.filter((cat: any) => !cat._id || cat._id === '');
        const existingCategories = data.categoryIDs.filter((cat: any) => cat._id && cat._id !== '');
        
        // For new state requirement, we need to create it first to get stateRequirementID
        // So we'll handle this differently
        
        if (editingRequirement?._id) {
          // Updating existing requirement
          
          // 1. Update all existing categories
          for (const cat of existingCategories) {
            await updateCategory(cat._id, {
              name: cat.name || cat.categoryTag,
              categoryTag: cat.categoryTag,
              minCredits: cat.minCredits,
              maxCredits: cat.maxCredits,
              description: cat.description
            });
            categoryIDs.push(cat._id);
          }
          
          // 2. Create new categories with stateRequirementID
          for (const cat of newCategories) {
            const createdCategory = await createCategory({
              name: cat.name || cat.categoryTag,
              categoryTag: cat.categoryTag,
              stateID: data.stateID || editingRequirement.stateID, // Use parent stateID
              professionID: data.professionID || editingRequirement.professionID, // Use parent professionID
              minCredits: cat.minCredits,
              maxCredits: cat.maxCredits,
              description: cat.description,
              stateRequirementID: editingRequirement._id
            });
            categoryIDs.push(createdCategory._id);
          }
        } else {
          // Creating new - categories will be created after state requirement
          categoryIDs = existingCategories.map((cat: any) => cat._id);
        }
      }
      
      // Prepare data with ObjectId references only
      const stateRequirementData: any = {
        ...data,
        categoryIDs: categoryIDs // Send only _id strings, not full objects
      };
      
      if (editingRequirement?._id) {
        // Update existing
        await updateStateRequirement(editingRequirement._id, stateRequirementData);
        setSuccessMessage('State requirement updated successfully!');
      } else {
        // Create new state requirement
        const result = await createStateRequirement(stateRequirementData);
        
        // Now create any new categories with the stateRequirementID
        if (data.categoryIDs && Array.isArray(data.categoryIDs)) {
          const newCategories = data.categoryIDs.filter((cat: any) => !cat._id || cat._id === '');
          for (const cat of newCategories) {
            await createCategory({
              name: cat.name || cat.categoryTag,
              categoryTag: cat.categoryTag,
              stateID: data.stateID,
              professionID: data.professionID,
              minCredits: cat.minCredits,
              maxCredits: cat.maxCredits,
              description: cat.description,
              stateRequirementID: result._id
            });
          }
        }
        
        setSuccessMessage('State requirement added successfully!');
      }
      setShowStateForm(false);
      setEditingRequirement(null);
      setSelectedStateID(null);
      if (formData.professionID) {
        fetchStateRequirements(formData.professionID);
      }
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      const errorMsg = err?.response?.data?.error || err?.message || 'Failed to save state requirement.';
      console.error('Save error:', err); // Debug log
      console.error('Error response:', err?.response?.data); // Debug log
      setError(errorMsg);
      alert(`Error saving state requirement: ${errorMsg}`); // Make error very visible
    }
  };

  const handleDeleteStateRequirement = async (requirementId: string) => {
    if (window.confirm('Are you sure you want to remove this state requirement?')) {
      try {
        await deleteStateRequirement(requirementId);
        setSuccessMessage('State requirement removed successfully!');
        if (formData.professionID) {
          fetchStateRequirements(formData.professionID);
        }
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err: any) {
        setError('Failed to delete state requirement.');
        console.error(err);
      }
    }
  };

  const handleCancelStateForm = () => {
    setShowStateForm(false);
    setEditingRequirement(null);
    setSelectedStateID(null);
  };

  const getStateName = (stateID: number): string => {
    const state = allStates.find(s => s.stateID === stateID);
    return state ? state.state : `State ${stateID}`;
  };

  const getAvailableStates = (): LicState[] => {
    const usedStateIDs = stateRequirements.map(r => r.stateID);
    return allStates.filter(s => !usedStateIDs.includes(s.stateID));
  };

  if (loading && !professions.length) return <div className="container mt-4">Loading professions...</div>;

  return (
    <div className="container mt-4">
      <h2 className="mb-4">Profession Management</h2>
      
      {error && <div className="alert alert-danger">{error}</div>}
      {successMessage && <div className="alert alert-success">{successMessage}</div>}

      <div className="row">
        {/* Professions List */}
        <div className="col-md-8">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">All Professions ({professions.length})</h5>
              <small className="text-muted">
                <i className="bi bi-info-circle"></i> Credit Term defines how learning units are labeled
              </small>
            </div>
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Credit Term</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {professions.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">No professions found</td>
                      </tr>
                    ) : (
                      professions.map((profession) => (
                        <tr key={profession._id} className={!profession.isActive ? 'table-secondary' : ''}>
                          <td><code>{profession.professionID}</code></td>
                          <td>
                            <strong>{profession.name}</strong>
                            {profession.description && (
                              <><br /><small className="text-muted">{profession.description}</small></>
                            )}
                          </td>
                          <td>
                            <span 
                              className="badge bg-info fs-6" 
                              title={`Credits displayed as "${profession.creditTerminology}" for ${profession.name} users`}
                              style={{ cursor: 'help' }}
                            >
                              {profession.creditTerminology}
                            </span>
                          </td>
                          <td>
                            {profession.isActive ? (
                              <span className="badge bg-success">Active</span>
                            ) : (
                              <span className="badge bg-secondary">Inactive</span>
                            )}
                          </td>
                          <td>
                            <button
                              className="btn btn-sm btn-primary me-2"
                              onClick={() => handleEdit(profession)}
                            >
                              Edit
                            </button>
                            {profession.isActive && (
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(profession._id)}
                              >
                                Deactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Add/Edit Form */}
        <div className="col-md-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{isEditing ? 'Edit Profession' : 'Add New Profession'}</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Profession ID <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="professionID"
                    value={formData.professionID}
                    onChange={handleInputChange}
                    placeholder="e.g., ARCH, PE, MD"
                    maxLength={10}
                    style={{ textTransform: 'uppercase' }}
                    disabled={!!isEditing}
                    required
                  />
                  <small className="text-muted">Unique identifier (uppercase)</small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Name <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Architect"
                    required
                  />
                </div>


                <div className="mb-3">
                  <label className="form-label">Credit Terminology <span className="text-danger">*</span></label>
                  <select
                    className="form-select"
                    name="creditTerminology"
                    value={formData.creditTerminology}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">-- Select Credit Type --</option>
                    <option value="LU">LU (Learning Units) - Architects</option>
                    <option value="PDH">PDH (Professional Development Hours) - Engineers</option>
                    <option value="CEU">CEU (Continuing Education Units)</option>
                    <option value="CE">CE (Continuing Education)</option>
                    <option value="CLE">CLE (Continuing Legal Education) - Attorneys</option>
                    <option value="CME">CME (Continuing Medical Education) - Healthcare</option>
                    <option value="CPE">CPE (Continuing Professional Education) - Accountants</option>
                    <option value="CSH">CSH (Credit Study Hours)</option>
                    <option value="CLU">CLU (Contact Learning Units)</option>
                    <option value="CPD">CPD (Continuing Professional Development)</option>
                  </select>
                  <small className="text-muted d-block mt-1">
                    This determines how credits are labeled throughout the system (e.g., "12 PDH" for PE users).
                    Choose the standard terminology for this profession.
                  </small>
                </div>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>

                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    name="isActive"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label" htmlFor="isActive">
                    Active
                  </label>
                </div>

                {/* State Requirements Section - Only show when editing */}
                {isEditing && formData.professionID && (
                  <div className="mb-3">
                    <hr />
                    <h6 className="mb-3">State Requirements</h6>
                    
                    {stateRequirements.length > 0 ? (
                      <div className="mb-3">
                        <div className="list-group">
                          {stateRequirements.map((req) => (
                            <div key={req._id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <h6 className="mb-1">{getStateName(req.stateID)}</h6>
                                  <small className="text-muted d-block">
                                    {req.totalCreditsRequired} {formData.creditTerminology || 'credits'} / {req.cycleLengthYears} years
                                    {req.allowCarryover && ' • Carryover allowed'}
                                  </small>
                                  {req.categoryIDs && req.categoryIDs.length > 0 && (
                                    <div className="mt-2">
                                      <small className="text-muted fw-bold">Categories:</small>
                                      <div className="d-flex flex-wrap gap-1 mt-1">
                                        {req.categoryIDs.map((cat, idx) => (
                                          <span key={idx} className="badge bg-secondary">
                                            {cat.categoryTag}: {cat.minCredits}
                                            {cat.maxCredits ? `-${cat.maxCredits}` : '+'} {formData.creditTerminology || 'credits'}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  {(req.stateprofessionlink1 || req.stateprofessionlink2) && (
                                    <div className="mt-2">
                                      <small className="text-muted fw-bold">Reference Links:</small>
                                      <div className="d-flex flex-column gap-1 mt-1">
                                        {req.stateprofessionlink1 && (
                                          <small>
                                            <a 
                                              href={req.stateprofessionlink1} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-primary"
                                            >
                                              <i className="bi bi-link-45deg"></i> {req.stateprofessionlink1desc || 'Link 1'}
                                            </a>
                                          </small>
                                        )}
                                        {req.stateprofessionlink2 && (
                                          <small>
                                            <a 
                                              href={req.stateprofessionlink2} 
                                              target="_blank" 
                                              rel="noopener noreferrer"
                                              className="text-primary"
                                            >
                                              <i className="bi bi-link-45deg"></i> {req.stateprofessionlink2desc || 'Link 2'}
                                            </a>
                                          </small>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => handleEditStateRequirement(req)}
                                  >
                                    <i className="bi bi-pencil"></i> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger"
                                    onClick={() => req._id && handleDeleteStateRequirement(req._id)}
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <p className="text-muted small">No state requirements added yet.</p>
                    )}

                    {getAvailableStates().length > 0 && (
                      <div className="input-group">
                        <select 
                          className="form-select" 
                          value={selectedStateID || ''}
                          onChange={(e) => setSelectedStateID(parseInt(e.target.value))}
                        >
                          <option value="">Select a state to add...</option>
                          {getAvailableStates().map((state) => (
                            <option key={state.stateID} value={state.stateID}>
                              {state.state}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => selectedStateID && handleAddState(selectedStateID)}
                          disabled={!selectedStateID}
                        >
                          <i className="bi bi-plus-lg"></i> Add State
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="d-grid gap-2">
                  <button type="submit" className="btn btn-primary">
                    {isEditing ? 'Update Profession' : 'Create Profession'}
                  </button>
                  {isEditing && (
                    <button type="button" className="btn btn-secondary" onClick={resetForm}>
                      Cancel Edit
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* State Requirement Form Modal */}
      {showStateForm && editingRequirement && selectedStateID && (
        <StateRequirementForm
          requirement={editingRequirement}
          stateName={getStateName(selectedStateID)}
          onSave={handleSaveStateRequirement}
          onCancel={handleCancelStateForm}
        />
      )}
    </div>
  );
};

export default ProfessionManagement;
